<template>
  <o-field>
    <o-autocomplete
      rounded
      v-model="clientSearchTerm"
      :data="filteredClients"
      field="name"
      placeholder="Search for a client"
      icon="search"
      icon-pack="fas"
      clearable
      open-on-focus
      @select="handleClientSelect"
      @input="filterClients"
      :loading="isSearchingClients"
      class="client-search-autocomplete"
      @blur="clearSearchState"
      expanded
    >
      <template #default="{ option }">
        <div class="media">
          <div class="media-content">
            <strong>{{ option.name }}</strong>
            <div class="is-size-7">
              {{ option.attribute_count }} dossier attributes, {{ option.doc_count }} documents
            </div>
          </div>
        </div>
      </template>
      <template #empty>No clients found</template>
    </o-autocomplete>
  </o-field>
</template>

<script>
export default {
  name: 'TypeaheadClientSearch',
  props: {
    selectedClientId: {
      type: String,
      default: '',
    },
    onSelect: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      clientSearchTerm: '',
      filteredClients: [],
      isSearchingClients: false,
      clientIds: [],
      searchTimeout: null,
    };
  },
  methods: {
    handleClientSelect(option) {
      console.log('Client selected:', option);
      if (option) {
        // Stop the spinner when a client is selected
        this.isSearchingClients = false;

        // Clear any pending search timeout
        if (this.searchTimeout) {
          clearTimeout(this.searchTimeout);
          this.searchTimeout = null;
        }

        this.clientSearchTerm = option.name;
        this.$emit('input', option.external_id);
        if (typeof this.onSelect === 'function') {
          this.onSelect(option);
        }
      } else {
        // If nothing was selected, ensure spinner is stopped
        this.isSearchingClients = false;
      }
    },
    clearSearchState() {
      // Ensure spinner and timeouts are cleared when component loses focus
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
      this.isSearchingClients = false;
    },
    filterClients(value) {
      // Clear previous timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }

      // If search term is empty, show all clients immediately without spinner
      if (!value || !value.trim()) {
        this.filteredClients = [...this.clientIds];
        this.isSearchingClients = false;
        return;
      }

      // Set loading state
      this.isSearchingClients = true;

      // Debounce the search to avoid excessive API calls
      this.searchTimeout = setTimeout(() => {
        try {
          // Perform client-side filtering with null checks
          const searchTerm = value.toLowerCase();
          console.log('Filtering clients with term:', searchTerm);
          this.filteredClients = this.clientIds.filter(client => 
            client && client.name && client.name.toLowerCase().includes(searchTerm),
          );
          console.log('Filtered clients:', this.filteredClients.length);
        } catch (error) {
          console.error('Error filtering clients:', error);
          // Fall back to showing all clients if there's an error
          this.filteredClients = [...this.clientIds];
        } finally {
          // Always ensure the spinner is stopped, even if there's an error
          this.isSearchingClients = false;
          this.searchTimeout = null;
        }
      }, 300); // 300ms debounce
    },
    async fetchClientIds() {
      try {
        this.isSearchingClients = true;
        const response = await fetch('/api/v1/docusign/doc-client-ids/');
        if (response.ok) {
          const data = await response.json();
          console.log('Loaded client data:', data);
          this.clientIds = data;
          this.filteredClients = [...this.clientIds];

          // If a client ID was already selected, set the search term
          if (this.selectedClientId) {
            const selectedClient = this.clientIds.find(client => client.external_id === this.selectedClientId);
            if (selectedClient) {
              this.clientSearchTerm = selectedClient.name;
            }
          }
        } else {
          console.error('Failed to fetch client IDs, status:', response.status);
        }
      } catch (error) {
        console.error('Error fetching client IDs:', error);
      } finally {
        // Make sure spinner is stopped after initialization
        this.isSearchingClients = false;
      }
    },
  },
  watch: {
    selectedClientId(newVal) {
      if (newVal) {
        const selectedClient = this.clientIds.find(client => 
          client && client.external_id === newVal,
        );
        if (selectedClient) {
          this.clientSearchTerm = selectedClient.name;
        }
      } else {
        this.clientSearchTerm = '';
      }
      // Always ensure spinner is stopped when selectedClientId changes
      this.isSearchingClients = false;
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
        this.searchTimeout = null;
      }
    },
  },
  mounted() {
    console.log('TypeaheadClientSearch mounted, fetching client data');
    this.fetchClientIds();
  },
  beforeDestroy() {
    // Clean up any remaining timeouts when component is destroyed
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
  },
};
</script>
<style>
.client-search-autocomplete {
  background-color: #2b2b2b !important;
  border-color: #444 !important;
  color: #ffffff !important;
}
.client-search-autocomplete input {
  background-color: #2b2b2b !important;
  border-color: #444 !important;
  color: #ffffff !important;
}
.client-search-autocomplete .o-acp__menu {
  background-color: #2b2b2b !important;
  border-color: #444 !important;
}
.client-search-autocomplete .o-acp__item {
  background-color: #2b2b2b !important;
  color: #ffffff !important;
}
.client-search-autocomplete .media-content {
  background-color: #2b2b2b !important;
  color: #ffffff !important;
}
.client-search-autocomplete .media-content .is-size-7 {
  color: #ffffff !important;
}


</style>
